import { zodResolver } from '@hookform/resolvers/zod';
import { Tag } from 'emblor';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../../design-system/button/button';
import { Form } from '../../../../../design-system/form/form-primitives';
import { Inline } from '../../../../../design-system/inline/inline';
import { Stack } from '../../../../../design-system/stack/stack';
import { InputTag } from '../../../../../design-system/tag-input/tag-input';

const formSchema = z.object({
  excludedSenders: z
    .array(
      z.object({
        id: z.string(),
        text: z.string().email({
          message: 'Please enter a valid email address',
        }),
      }),
    )
    .superRefine((items, ctx) => {
      const texts = items.map((i) => i.text.toLowerCase());

      items.forEach((item, index) => {
        if (texts.indexOf(item.text.toLowerCase()) !== index) {
          ctx.addIssue({
            code: 'custom',
            message: 'This email address has already been added.',
            path: [index, 'text'],
          });
        }
      });
    }),
});

type FormValues = z.infer<typeof formSchema>;

type ExcludeSendersFormProps = {
  isLoading: boolean;
  excludedSenders: string[];
  onSubmit: (values: string[]) => Promise<void>;
  onCancel: () => void;
};

export const ExcludeSendersForm = ({ isLoading, excludedSenders, onSubmit, onCancel }: ExcludeSendersFormProps) => {
  const [tags, setTags] = useState<Tag[]>(excludedSenders.map((sender) => ({ id: sender, text: sender })));
  const [activeTagIndex, setActiveTagIndex] = useState<number | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      excludedSenders: tags,
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((values) => {
          const emailList = values.excludedSenders.map((tag) => tag.text);

          onSubmit(emailList);
        })}
      >
        <Stack space={2} align="stretch">
          <Stack space={1} align="stretch">
            <Inline justify="between">
              <h3 className="text-lg font-medium leading-6 tracking-tight text-black">Exclude senders</h3>

              <Inline space={2}>
                <Button variant="default" type="submit" isLoading={isLoading}>
                  Save
                </Button>

                <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
                  Cancel
                </Button>
              </Inline>
            </Inline>

            <p className="text-sm font-medium leading-6 tracking-tight text-seapoint-gray-text">
              Here is the list of senders that Seapoint shouldn't automatically pull invoices from.
            </p>
          </Stack>

          <Controller
            name="excludedSenders"
            control={form.control}
            render={({ field }) => (
              <InputTag
                tags={tags}
                setTags={async (values) => {
                  form.setValue(field.name, values as [Tag, ...Tag[]]);
                  const isValid = await form.trigger(field.name);

                  if (isValid) {
                    setTags(values);
                  }
                }}
                activeTagIndex={activeTagIndex}
                setActiveTagIndex={setActiveTagIndex}
                inputName={field.name}
                placeholder="Type email to exclude sender"
                allowDuplicates
                disabled={isLoading}
                addTagsOnBlur
              />
            )}
          />
        </Stack>
      </form>
    </Form>
  );
};
